<script setup lang="ts">
import { diContainer } from '@/coreContainer/container';
import { IOrderBasis } from '@/coreDomainsModule/entities/orderBasis';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { LoadingState } from '@/coreDomainsModule/uiLoadingState';
import InfoModal from '@/corePagesModule/components/InfoModal.vue';
import { i18n } from '@/i18n';
import { LottieAnimation } from 'lottie-web-vue';
import { computed, ref, watch } from 'vue';
import loadingAnimation from '@/assets/loadingAnimation.json';
import Mdi from '@/corePagesModule/components/Mdi.vue';
import OrderBasisDetailSection from './OrderBasisDetailSection.vue';

const t = i18n.t;

const props = defineProps<{ aggregateBusinessId: string }>();
const emits = defineEmits<{ (e: 'close') }>();

const loadingState = ref<LoadingState>({ status: { type: 'none' } });
const orderBases = ref<IOrderBasis[]>([]);
const orderBasisItems = computed(() =>
  orderBases.value.flatMap((basis) =>
    basis.sourceOrderInfos.map((info) => ({ ...info, orderBasisId: basis.orderBasisId, updatedAt: basis.updatedAt }))
  )
);
const selectedSourceOrderId = ref<string>('');

const selectedOrderBasisId = computed(
  () => orderBasisItems.value.find((item) => item.sourceOrderId === selectedSourceOrderId.value)?.orderBasisId
);
const selectedOrderBasis = computed(() => orderBases.value.find((basis) => basis.orderBasisId === selectedOrderBasisId.value));

/**
 * データフェッチ
 */
const fetch = async () => {
  if (!props.aggregateBusinessId) return;
  loadingState.value.status = { type: 'loading' };
  const result = await diContainer.getOrderBasesByAggregateUseCase.execute(props.aggregateBusinessId);
  if (result.status.type === 'error') {
    loadingState.value.status = { type: 'error', errorMessage: 'fetchError' };
    return;
  }
  orderBases.value = result.status.data;
  if (!result.status.data.length) {
    loadingState.value.status = { type: 'zeroMatch' };
    return;
  }
  loadingState.value.status = { type: 'success' };
  return;
};

/**
 * 発注根拠を選択する
 * @param selected - 選択した発注根拠ID
 */
const selectOrderBasis = (selected: string) => {
  selectedSourceOrderId.value = selected;
};

/**
 * 日付を表示用文字列に変換する
 * @param date - ミリ秒UnixTime
 */
const formatDate = (date?: number) => {
  return date ? DateService.convertMSUnixTimeToYYYYMMDDSlash(date) : '--';
};

watch(
  () => props.aggregateBusinessId,
  async (newValue) => {
    selectedSourceOrderId.value = '';
    orderBases.value = [];
    if (!newValue) return;
    await fetch();
  },
  { immediate: true }
);
</script>
<template>
  <InfoModal
    :modal-state="!!props.aggregateBusinessId"
    :modal-width="1400"
    enable-outside-click
    :back-button-label="t('閉じる')"
    @on-back="emits('close')"
  >
    <template #title>
      {{ t('発注根拠一覧') }}
    </template>
    <template #description>
      {{ t('集計に紐づく発注根拠を確認できます。') }}<br v-if="i18n.locale === 'en'" />{{ t('(表示されているのは根拠作成時の情報です)') }}
    </template>
    <template #default>
      <div class="order-basis-modal__container">
        <div v-if="loadingState.status.type === 'loading'" class="non-success">
          <LottieAnimation :animation-data="loadingAnimation" :loop="true" :auto-play="true" />
          <p class="non-success__message">
            {{ t('ローディング中...') }}
          </p>
        </div>
        <div v-else-if="loadingState.status.type === 'error'" class="non-success">
          <Mdi icon="error" variant="outlined" class="non-success__icon non-success__icon--error" />
          <p class="non-success__message">
            {{ t('エラーが発生しました') }}
          </p>
        </div>
        <div v-else-if="loadingState.status.type === 'zeroMatch'" class="non-success">
          <Mdi icon="inbox" class="non-success__icon" />
          <p class="non-success__message">
            {{ t('発注根拠がありません') }}
          </p>
        </div>
        <div v-else class="order-basis-modal">
          <div class="list-view">
            <button
              v-for="basis in orderBasisItems"
              :key="basis.sourceOrderId"
              class="list-view__item"
              :class="{ 'list-view__item--selected': selectedOrderBasisId === basis.orderBasisId }"
              type="button"
              @click="selectOrderBasis(basis.sourceOrderId)"
            >
              <span class="list-view__item-header">
                <span class="list-view__stat-label">{{ t('発注根拠作成日') }}</span>
                <span class="list-view__updated-at">{{ formatDate(basis.updatedAt) }}</span>
              </span>
              <span class="list-view__stats">
                <span class="list-view__stat">
                  <span class="list-view__stat-label">{{ t('発注数量') }}</span>
                  <span class="list-view__stat-value">{{ basis.initialOrderAmount.addComma() }}</span>
                </span>
                <span class="list-view__stat">
                  <span class="list-view__stat-label">{{ t('希望納期') }}</span>
                  <span class="list-view__stat-value">{{
                    DateService.convertMSUnixTimeToYYYYMMDDSlash(basis.initialDesiredDeliveryDate)
                  }}</span>
                </span>
              </span>
            </button>
          </div>
          <div v-if="!selectedOrderBasisId" class="detail-view--unselected">
            {{ t('選択してください') }}
          </div>
          <div v-else class="detail-view">
            <OrderBasisDetailSection :basis-data="selectedOrderBasis" :source-order-id="selectedSourceOrderId" />
          </div>
        </div>
      </div>
    </template>
  </InfoModal>
</template>
<style lang="scss" scoped>
.order-basis-modal {
  display: grid;
  grid-template-columns: 300px minmax(0, 1fr);
  border-radius: 4px;
  overflow: hidden;
  *:not(.material-icons) {
    font-family: 'Noto Sans JP';
  }

  &__container {
    min-height: 400px;
    min-width: 0;
  }
}
.list-view {
  display: grid;
  align-content: start;
  gap: 8px;
  overflow-y: auto;
  padding: 8px;
  background-color: $surfaceSecondary;

  &__item {
    display: grid;
    gap: 10px;
    width: 100%;
    min-width: 0;
    padding: 12px;
    border: 1px solid $borderDefault;
    border-radius: 8px;
    background-color: $semanticWhite;
    cursor: pointer;
    text-align: left;
    transition: border-color 0.2s, box-shadow 0.2s, background-color 0.2s;

    &:hover {
      border-color: $semanticPrimary;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }

    &--selected {
      border-color: $semanticPrimary;
      background-color: $semanticPrimarySubdued;
      box-shadow: inset 4px 0 0 $semanticPrimary;
    }
  }

  &__item-header {
    display: grid;
    gap: 4px;
    min-width: 0;
  }

  &__updated-at {
    min-width: 0;
    color: $textDefault;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.3;
  }

  &__source-count {
    display: grid;
    grid-template-columns: max-content minmax(0, 1fr);
    align-items: baseline;
    gap: 8px;
  }

  &__source-count-label {
    color: $textSecondary;
    font-size: 12px;
    line-height: 1.3;
  }

  &__source-count-value {
    color: $textDefault;
    font-size: 13px;
    font-weight: 700;
    line-height: 1.3;
  }

  &__stats {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }

  &__stat {
    display: grid;
    gap: 4px;
    min-width: 0;
  }

  &__stat-label {
    color: $textSecondary;
    font-size: 11px;
    line-height: 1;
  }

  &__stat-value {
    min-width: 0;
    overflow: hidden;
    color: $textDefault;
    font-size: 13px;
    font-weight: 700;
    line-height: 1.2;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.detail-view {
  background-color: $semanticPrimarySubdued;
  padding: 16px;
  min-width: 0;

  &--unselected {
    background-color: $semanticPrimarySubdued;
    padding: 16px;
    display: grid;
    justify-content: center;
    align-items: center;
    font-size: 16px;
    color: $textSecondary;
    font-weight: 500;
  }
}

.non-success {
  padding-top: 100px;
  display: grid;
  grid-template-rows: max-content max-content;
  justify-content: center;
  align-content: center;
  gap: 20px;

  &__icon {
    text-align: center;
    font-size: 64px;
    color: $textSecondary;
    opacity: 0.6;

    &--error {
      color: $semanticError;
    }
  }

  &__message {
    text-align: center;
    font-size: 16px;
    color: $textSecondary;
    font-weight: 500;
  }
}
</style>
